export type OnboardingStepKey =
  | 'installation'
  | 'installationPending'
  | 'personalInfo'
  | 'useCases'
  | 'invite'
  | 'repoSelection'
  | 'analyzerSelection'
  | 'runAnalysis'

export interface OnboardingStep {
  key: OnboardingStepKey
  routeName: string
  enabled: boolean
  title?: string
  subtitle?: string
}

export const ONBOARDING_FEATURES = {
  enablePersonalInfo: true,
  enableUseCases: true,
  enablePrSelection: true
}

export const ONBOARDING_FLOW: OnboardingStep[] = [
  {
    key: 'installation',
    routeName: 'installation-provider',
    enabled: true
  },
  {
    key: 'personalInfo',
    routeName: 'onboard-provider-owner-personal-info',
    enabled: ONBOARDING_FEATURES.enablePersonalInfo,
    title: 'Let’s get to know you better',
    subtitle:
      'We’re excited to have you on DeepSource! Tell us some more about yourself and your team so we can tailor your experience.'
  },
  {
    key: 'useCases',
    routeName: 'onboard-provider-owner-use-cases',
    enabled: ONBOARDING_FEATURES.enableUseCases,
    title: 'How would you like to use DeepSource?',
    subtitle:
      'DeepSource’s AI Code Review platform helps you ship clean and secure code. Select what’s the most relevant to you right now — this helps us personalize your dashboard.'
  },
  {
    key: 'invite',
    routeName: 'onboard-provider-owner-invite',
    enabled: true,
    title: 'Who would you like to invite?',
    subtitle: 'Add your colleagues to your DeepSource workspace for their PRs to be reviewed.'
  },
  {
    key: 'repoSelection',
    routeName: 'onboard-provider-owner-repo',
    enabled: true,
    title: 'Select a repository to start review',
    subtitle:
      'DeepSource will review the existing codebase and all new pull requests. Start with selecting your first repository to review.'
  },
  {
    key: 'analyzerSelection',
    routeName: 'onboard-provider-owner-repo-analyzers',
    enabled: true,
    title: 'Confirm analyzers to run on this repository',
    subtitle:
      "Select as many analyzers depending on the programming languages you're using. You can change this anytime later from the repository's settings."
  },
  {
    key: 'runAnalysis',
    routeName: 'onboard-provider-owner-repo-run',
    enabled: true
  }
]

export const getEnabledOnboardingSteps = (): OnboardingStep[] =>
  ONBOARDING_FLOW.filter((step) => step.enabled)

export const getOnboardingStepByRouteName = (routeName: string): OnboardingStep | undefined =>
  ONBOARDING_FLOW.find((step) => step.routeName === routeName)

export const getFirstEnabledOnboardingStep = (): OnboardingStep | undefined => {
  return getEnabledOnboardingSteps()[0]
}

export const getFirstPostInstallOnboardingStep = (): OnboardingStep | undefined => {
  return getEnabledOnboardingSteps().find(
    (step) => step.key !== 'installation' && step.key !== 'installationPending'
  )
}

export const getNextEnabledOnboardingStep = (routeName: string): OnboardingStep | undefined => {
  const enabledSteps = getEnabledOnboardingSteps()
  const currentIndex = enabledSteps.findIndex((step) => step.routeName === routeName)
  if (currentIndex === -1) return undefined
  return enabledSteps[currentIndex + 1]
}

export const getOnboardingPageMetaMap = (): Record<string, { title: string; subtitle: string }> => {
  return ONBOARDING_FLOW.reduce<Record<string, { title: string; subtitle: string }>>(
    (acc, step) => {
      if (step.title && step.subtitle) {
        acc[step.routeName] = {
          title: step.title,
          subtitle: step.subtitle
        }
      }
      return acc
    },
    {}
  )
}
